/**
 * Phase 0 reference preset.
 *
 * These are the hand-tuned magic numbers that define the Phase 0 visual.
 * Per the plan's anti-regression rule, these values must be preserved
 * across all future refactors -- if a Phase 1 engine refactor renders this
 * preset less well than Phase 0 does, the refactor is rejected.
 *
 * Tweak with intent. The HUD's render-scale and present-mode toggles do not
 * count as "the preset" -- they're inspection tools.
 */

import type { WarpParams } from '../renderer/webgpu/passes/feedbackWarpPass';
import { WarpKind } from '../renderer/webgpu/passes/feedbackWarpPass';
import type { SeedParams } from '../renderer/webgpu/passes/seedPass';
import type { PresentParams } from '../renderer/webgpu/passes/presentPass';
import { PresentMode } from '../renderer/webgpu/passes/presentPass';

export const PHASE0_NAME = 'phase0_reference';

export const PHASE0_WARP: WarpParams = {
  kind: WarpKind.LegacyPhase0,
  // Slight outward push (positive = inward zoom of the source UV). The image
  // appears to flow outward, like Geiss "into the screen" tunnels.
  baseZoom: 0.012,
  bassZoomGain: 0.045,

  // Sub-degree spiral.
  rotation: 0.003,
  bassRotationGain: 0.012,

  // Soft radial sin keeps motion from feeling mechanical.
  ripple: 0.0035,
  rippleFreq: 7.5,

  // Decay slightly under 1 so trails fade rather than burn.
  decay: 0.985,

  // Light chromatic aberration -- gives feedback edges a colored bloom.
  chroma: 0.0028,

  // Tiny per-frame hue rotation.
  hueShift: 0.014,

  // Beat impulse: a brief extra zoom that locks back in the next frame.
  beatPunch: 0.06,

  // Slow drift of the warp center so the tunnel doesn't pin to a pixel.
  centerDriftAmp: 0.08,
  centerDriftRate: 0.18,

  // No warp field for the Phase 0 reference -- it uses procedural maths.
  fieldT: 1,
  fieldStrength: 0,
};

export const PHASE0_SEED: SeedParams = {
  baseY: 0.0,
  gain: 0.55,
  halfThick: 0.012,
  intensity: 0.75,
  color: [0.55, 0.85, 1.0], // cool cyan-white seed
  edgeSharp: 9.0,
};

export const PHASE0_PRESENT: PresentParams = {
  mode: PresentMode.Normal,
  tonemapEnabled: true,
  gammaEnabled: true,
  exposure: 1.05,
  vignette: 0.18,
  // Phase 0 baseline: no post FX. Anti-regression rule says this preset
  // must look identical to the spike, so don't add bloom or grain here.
  bloomEnabled: false,
  bloomIntensity: 0,
  grain: 0,
};

export const PHASE0_INTERNAL_SCALE = 0.75;
